/**
 * Progress-chart aggregation (spec §11, and the granularity ladder in the superseded
 * spec §6).
 *
 *   month   one point per gym day   bar
 *   3M      one point per gym day   bar
 *   6M      weekly totals           bar
 *   All     monthly totals          line
 *
 * `aggregate` emits gym days only: a bucket with no gym day in it is never emitted.
 * `withGaps` then puts the missed buckets back as empty points (B-58, D-109, reversing
 * spec §11's "days with no workout are never plotted"), so a missed day is a visible slot on the
 * axis rather than two bars sitting closer together than the calendar says they were.
 * The two are separate so every invariant `aggregate` was tested against still holds of
 * it unchanged, and a caller that wants gym days only still has them.
 *
 * `today` is passed in as an already-resolved local date string. Core does not read a
 * clock and does not know the user's timezone (C-13).
 */
import { addDays, addMonths, monthKey, startOfMonth, startOfWeek } from "./dates.js";
import { scoreWorkout } from "./score.js";
export const CHART_GRANULARITY = {
    month: "day",
    "3M": "day",
    "6M": "week",
    all: "month",
};
/**
 * Aggregate workouts into chart points, oldest first.
 *
 * Scores are recomputed with `scoreWorkout` rather than read from the denormalised
 * `score` field (D-36), so the chart cannot be wrong in the one place a stale value
 * would be least visible.
 */
export function aggregate(workouts, range, today) {
    const cutoff = rangeStart(range, today);
    const buckets = new Map();
    for (const workout of workouts) {
        if (cutoff !== null && workout.date < cutoff)
            continue;
        if (workout.date > today)
            continue;
        const bucket = bucketKey(workout.date, range);
        const existing = buckets.get(bucket);
        const score = scoreWorkout(workout);
        if (existing) {
            existing.score += score;
            existing.dayCount += 1;
        }
        else {
            buckets.set(bucket, { bucket, score, dayCount: 1 });
        }
    }
    // Bucket keys are zero-padded, so lexicographic order is chronological order.
    return [...buckets.values()].sort((a, b) => (a.bucket < b.bucket ? -1 : a.bucket > b.bucket ? 1 : 0));
}
/**
 * `aggregate`'s points with every missed bucket between them put back, as a point with
 * score 0 and dayCount 0, oldest first.
 *
 * The axis runs from the range's first bucket (for All, the first gym day's month) to
 * today's. At the per-day ranges today is an exception: it is only on the axis once
 * something is logged on it, because a day still in progress has not been missed.
 *
 * No points in, no points out — an empty range stays empty rather than becoming a row
 * of missed days, so "Nothing logged this month" is still what the screen can say.
 */
export function withGaps(points, range, today) {
    if (points.length === 0)
        return [];
    const byBucket = new Map(points.map((p) => [p.bucket, p]));
    const cutoff = rangeStart(range, today);
    const first = cutoff === null ? points[0].bucket : bucketKey(cutoff, range);
    const perDay = CHART_GRANULARITY[range] === "day";
    const last = perDay && !byBucket.has(today) ? addDays(today, -1) : bucketKey(today, range);
    const filled = [];
    // Bucket keys are zero-padded, so string comparison is chronological order.
    for (let bucket = first; bucket <= last; bucket = nextBucket(bucket, range)) {
        filled.push(byBucket.get(bucket) ?? { bucket, score: 0, dayCount: 0 });
    }
    return filled;
}
function nextBucket(bucket, range) {
    switch (CHART_GRANULARITY[range]) {
        case "day":
            return addDays(bucket, 1);
        case "week":
            return addDays(bucket, 7);
        case "month":
            return monthKey(addMonths(`${bucket}-01`, 1));
    }
}
/** The inclusive first date of a range, or null for All. */
export function rangeStart(range, today) {
    switch (range) {
        case "month":
            return startOfMonth(today);
        case "3M":
            return addMonths(today, -3);
        case "6M":
            return addMonths(today, -6);
        case "all":
            return null;
    }
}
/**
 * The bucket a date falls in: the date itself at month and 3M, the week's Monday at 6M,
 * the month at All.
 *
 * Exported because Progress's ledger highlight has to know which rows a tapped week or
 * month bar stands for, and asking this rather than re-deriving it is what keeps the
 * highlighted rows and the bar describing the same set of days (D-09).
 */
export function bucketKey(date, range) {
    switch (range) {
        case "month":
            return date;
        case "3M":
            return date;
        case "6M":
            return startOfWeek(date);
        case "all":
            return monthKey(date);
    }
}
//# sourceMappingURL=chart.js.map